import { useMemo } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Braces,
  CheckCircle2,
  ChevronDown,
  Info,
  Loader2,
  SlidersHorizontal,
  TrendingUp,
  X,
} from 'lucide-react';
import { useSyntaxAnalysis } from '../../../spatial/contexts/SyntaxAnalysisContext';
import {
  ANALYSIS_HELP_TEXT,
  DERIVED_ANALYSIS_LABEL,
  HIERARCHY_PROFILE_DESCRIPTIONS,
  HIERARCHY_PROFILE_LABELS,
  NETWORK_ANALYSIS_INDICATOR_DESCRIPTIONS,
  NETWORK_ANALYSIS_INDICATOR_GROUPS,
  NETWORK_ANALYSIS_INDICATOR_LABELS,
  NETWORK_ANALYSIS_LABEL,
  useNetworkAnalysisUiStore,
  type NetworkAnalysisIndicatorId,
} from '../../../spatial';
import {
  COLOR_RAMPS,
  DEFAULT_SYNTAX_RAMP,
  type SyntaxRampName,
} from '../../../spatial/config/syntax-color-ramps';
import type { MetricRange } from '../../../spatial/services/syntax-api.service';
import type {
  HierarchyProfile,
  NetworkHierarchyResponse,
} from '../../../spatial/services/network-hierarchy-api.service';
import './network-analysis-overview-panel.css';

export interface NetworkAnalysisOverviewPanelProps {
  hierarchyData: NetworkHierarchyResponse | null;
  hierarchyLoading: boolean;
  hierarchyError: string | null;
  hierarchyProfile: HierarchyProfile;
  onHierarchyProfileChange: (profile: HierarchyProfile) => void;
  onClose: () => void;
  colorRamp?: SyntaxRampName;
}

function isStructureIndicator(
  indicator: NetworkAnalysisIndicatorId,
): indicator is 'structure' {
  return indicator === 'structure';
}

function formatMetricValue(value: number | null | undefined, decimals = 3): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/D';
  }

  if (value === 0) return '0';
  if (Math.abs(value) < 0.01) return value.toExponential(2);
  if (Math.abs(value) < 1) return value.toFixed(Math.max(decimals, 3));
  if (Math.abs(value) < 100) return value.toFixed(2);
  return value.toFixed(0);
}

function getMetricRangeStep(min: number, max: number): number {
  const span = Math.abs(max - min);
  if (span <= 0) {
    return 0.001;
  }

  return Math.max(span / 200, 0.0001);
}

function clampVisualRange(
  dataRange: MetricRange | null,
  override: MetricRange | null,
): MetricRange | null {
  if (!dataRange) {
    return null;
  }

  if (!override) {
    return dataRange;
  }

  const min = Math.max(dataRange.min, Math.min(override.min, override.max));
  const max = Math.min(dataRange.max, Math.max(override.min, override.max));

  if (min === max) {
    return dataRange;
  }

  return { min, max };
}

function getStructureDataRange(
  hierarchyData: NetworkHierarchyResponse | null,
): MetricRange | null {
  if (!hierarchyData?.features.length) {
    return null;
  }

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (const feature of hierarchyData.features) {
    const value = feature.properties.combinedPercentile;
    if (Number.isFinite(value)) {
      min = Math.min(min, value);
      max = Math.max(max, value);
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }

  if (min === max) {
    return {
      min: Math.max(0, min - 0.01),
      max: Math.min(1, max + 0.01),
    };
  }

  return { min, max };
}

export function NetworkAnalysisOverviewPanel({
  hierarchyData,
  hierarchyLoading,
  hierarchyError,
  hierarchyProfile,
  onHierarchyProfileChange,
  onClose,
  colorRamp = DEFAULT_SYNTAX_RAMP,
}: NetworkAnalysisOverviewPanelProps): JSX.Element {
  const {
    status,
    selectedMetric,
    setSelectedMetric,
    currentMetricDataRange,
    currentMetricVisualRange,
    contrastExponent,
    setCurrentMetricVisualRange,
    resetCurrentMetricVisualRange,
    setContrastExponent,
    calculatedAt,
    error,
    syntaxVersion,
    syntaxBasedOnNetworkVersion,
    urbanNetworkVersion,
    isStale,
  } = useSyntaxAnalysis();
  const {
    analysisActive,
    activeIndicator,
    setActiveIndicator,
    structureVisualRangeOverride,
    setStructureVisualRangeOverride,
    resetStructureVisualRangeOverride,
    structureContrastExponent,
    setStructureContrastExponent,
  } = useNetworkAnalysisUiStore();

  const isStructureActive = isStructureIndicator(activeIndicator);
  const isPopulationActive = activeIndicator === 'population';
  const structureDataRange = useMemo(
    () => getStructureDataRange(hierarchyData),
    [hierarchyData],
  );
  const structureVisualRange = useMemo(
    () => clampVisualRange(structureDataRange, structureVisualRangeOverride),
    [structureDataRange, structureVisualRangeOverride],
  );
  const activeDataRange = isStructureActive ? structureDataRange : currentMetricDataRange;
  const activeVisualRange = isStructureActive ? structureVisualRange : currentMetricVisualRange;
  const activeContrastExponent = isStructureActive
    ? structureContrastExponent
    : contrastExponent;

  const metricStep = useMemo(() => {
    if (!activeDataRange) {
      return 0.001;
    }

    return getMetricRangeStep(activeDataRange.min, activeDataRange.max);
  }, [activeDataRange]);

  const hasCustomVisualRange = Boolean(
    activeDataRange &&
      activeVisualRange &&
      (
        Math.abs(activeDataRange.min - activeVisualRange.min) > metricStep ||
        Math.abs(activeDataRange.max - activeVisualRange.max) > metricStep
      ),
  );

  const syntaxRamp = COLOR_RAMPS[colorRamp];
  const activeRamp = isStructureActive ? COLOR_RAMPS.viridis : syntaxRamp;
  const activeGradientStops = activeRamp.stops
    .map((stop) => `${stop.color} ${stop.stop * 100}%`)
    .join(', ');

  const handleIndicatorChange = (indicator: NetworkAnalysisIndicatorId): void => {
    setActiveIndicator(indicator);

    if (!isStructureIndicator(indicator) && indicator !== 'population' && selectedMetric !== indicator) {
      setSelectedMetric(indicator);
    }
  };

  const handleVisualMinChange = (value: number): void => {
    if (!activeVisualRange) {
      return;
    }

    const nextRange = {
      min: Math.min(value, activeVisualRange.max - metricStep),
      max: activeVisualRange.max,
    };

    if (isStructureActive) {
      setStructureVisualRangeOverride(nextRange);
      return;
    }

    setCurrentMetricVisualRange(nextRange);
  };

  const handleVisualMaxChange = (value: number): void => {
    if (!activeVisualRange) {
      return;
    }

    const nextRange = {
      min: activeVisualRange.min,
      max: Math.max(value, activeVisualRange.min + metricStep),
    };

    if (isStructureActive) {
      setStructureVisualRangeOverride(nextRange);
      return;
    }

    setCurrentMetricVisualRange(nextRange);
  };

  const handleResetVisualRange = (): void => {
    if (isStructureActive) {
      resetStructureVisualRangeOverride();
      setStructureContrastExponent(1);
      return;
    }

    resetCurrentMetricVisualRange();
    setContrastExponent(1);
  };

  return (
    <div className="network-analysis-overview-panel">
      <header className="network-analysis-overview-panel__header">
        <div className="network-analysis-overview-panel__title-group">
          <Braces size={18} />
          <div>
            <h2 className="network-analysis-overview-panel__title">{NETWORK_ANALYSIS_LABEL}</h2>
            <p className="network-analysis-overview-panel__subtitle">
              Integration, Choice y síntesis estructural en una única lectura de red
            </p>
          </div>
        </div>
        <button
          type="button"
          className="network-analysis-overview-panel__close-btn"
          onClick={onClose}
          aria-label="Cerrar overview analítico"
          title="Cerrar panel"
        >
          <X size={16} />
        </button>
      </header>

      <div className="network-analysis-overview-panel__content">
        <div className="network-analysis-overview-panel__section-list">
          <section className="network-analysis-overview-panel__section">
            <div className="network-analysis-overview-panel__section-header">
              <h3 className="network-analysis-overview-panel__section-title">Indicador activo</h3>
              <span
                className={`network-analysis-overview-panel__badge ${
                  analysisActive ? 'is-active' : 'is-inactive'
                }`}
              >
                {analysisActive ? 'Mapa activo' : 'Mapa inactivo'}
              </span>
            </div>

            <div className="network-analysis-overview-panel__metric-groups">
              {NETWORK_ANALYSIS_INDICATOR_GROUPS.map((group) => (
                <div key={group.title} className="network-analysis-overview-panel__metric-group">
                  <div className="network-analysis-overview-panel__metric-group-title">
                    {group.title}
                  </div>
                  <div className="network-analysis-overview-panel__metric-grid">
                    {group.items.map((indicator) => (
                      <button
                        key={indicator}
                        type="button"
                        className={`network-analysis-overview-panel__metric-btn ${
                          activeIndicator === indicator ? 'active' : ''
                        }`}
                        onClick={() => handleIndicatorChange(indicator)}
                        title={NETWORK_ANALYSIS_INDICATOR_DESCRIPTIONS[indicator]}
                      >
                        {NETWORK_ANALYSIS_INDICATOR_LABELS[indicator]}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="network-analysis-overview-panel__highlight-card">
              <span className="network-analysis-overview-panel__highlight-label">
                Indicador seleccionado
              </span>
              <strong className="network-analysis-overview-panel__highlight-title">
                {NETWORK_ANALYSIS_INDICATOR_LABELS[activeIndicator]}
              </strong>
              <p className="network-analysis-overview-panel__highlight-text">
                {NETWORK_ANALYSIS_INDICATOR_DESCRIPTIONS[activeIndicator]}
              </p>
            </div>
          </section>

          <section className="network-analysis-overview-panel__section">
            <div className="network-analysis-overview-panel__section-header">
              <h3 className="network-analysis-overview-panel__section-title">Estado</h3>
              {isStructureActive && (
                <span className="network-analysis-overview-panel__section-pill">
                  <TrendingUp size={12} />
                  Indicador derivado
                </span>
              )}
            </div>

            {isPopulationActive && (
              <p className="network-analysis-overview-panel__hint">
                El estado y los controles de población están disponibles en el panel izquierdo. Hacé click en un segmento del mapa para ver su detalle.
              </p>
            )}

            {!isStructureActive && !isPopulationActive && status === 'ready' && !isStale && (
              <div className="network-analysis-overview-panel__status network-analysis-overview-panel__status--valid">
                <CheckCircle2 size={14} />
                <span>Sintaxis vigente sobre la red urbana actual.</span>
              </div>
            )}
            {!isStructureActive && !isPopulationActive && status === 'ready' && isStale && (
              <div className="network-analysis-overview-panel__status network-analysis-overview-panel__status--warning">
                <AlertTriangle size={14} />
                <span>La sintaxis quedó desactualizada respecto a la última edición de red.</span>
              </div>
            )}
            {!isStructureActive && !isPopulationActive && status === 'calculating' && (
              <div className="network-analysis-overview-panel__status network-analysis-overview-panel__status--loading">
                <Loader2 size={14} className="network-analysis-overview-panel__spinner" />
                <span>Calculando métricas de sintaxis...</span>
              </div>
            )}
            {!isStructureActive && !isPopulationActive && status === 'error' && (
              <div className="network-analysis-overview-panel__status network-analysis-overview-panel__status--error">
                <AlertCircle size={14} />
                <span>{error || 'Error en el cálculo de sintaxis.'}</span>
              </div>
            )}
            {!isStructureActive && !isPopulationActive && status === 'idle' && (
              <p className="network-analysis-overview-panel__hint">
                El análisis se habilita cuando el proyecto ya cuenta con una corrida válida de sintaxis.
              </p>
            )}

            {isStructureActive && hierarchyLoading && (
              <div className="network-analysis-overview-panel__status network-analysis-overview-panel__status--loading">
                <Loader2 size={14} className="network-analysis-overview-panel__spinner" />
                <span>Cargando {DERIVED_ANALYSIS_LABEL.toLowerCase()}...</span>
              </div>
            )}
            {isStructureActive && hierarchyError && !hierarchyLoading && (
              <div className="network-analysis-overview-panel__status network-analysis-overview-panel__status--error">
                <AlertCircle size={14} />
                <span>{hierarchyError}</span>
              </div>
            )}
            {isStructureActive && hierarchyData && !hierarchyLoading && !hierarchyError && (
              <div className="network-analysis-overview-panel__status network-analysis-overview-panel__status--valid">
                <CheckCircle2 size={14} />
                <span>{DERIVED_ANALYSIS_LABEL} disponible como síntesis estructural del mismo análisis.</span>
              </div>
            )}
            {isStructureActive && !hierarchyData && !hierarchyLoading && !hierarchyError && (
              <p className="network-analysis-overview-panel__hint">
                {DERIVED_ANALYSIS_LABEL} depende de una sintaxis válida y resume el peso estratégico del tramo.
              </p>
            )}
          </section>

          <section className="network-analysis-overview-panel__section">
            <h3 className="network-analysis-overview-panel__section-title">Ayuda rápida</h3>
            <div className="network-analysis-overview-panel__help-list">
              <div className="network-analysis-overview-panel__help-item">
                <Info size={13} />
                <div>
                  <strong>Integration</strong>
                  <span>{ANALYSIS_HELP_TEXT.integration}</span>
                </div>
              </div>
              <div className="network-analysis-overview-panel__help-item">
                <Info size={13} />
                <div>
                  <strong>Choice</strong>
                  <span>{ANALYSIS_HELP_TEXT.choice}</span>
                </div>
              </div>
              <div className="network-analysis-overview-panel__help-item">
                <Info size={13} />
                <div>
                  <strong>Global y radios</strong>
                  <span>{ANALYSIS_HELP_TEXT.global} {ANALYSIS_HELP_TEXT.radius}</span>
                </div>
              </div>
              <div className="network-analysis-overview-panel__help-item">
                <Info size={13} />
                <div>
                  <strong>{DERIVED_ANALYSIS_LABEL}</strong>
                  <span>{ANALYSIS_HELP_TEXT.structure}</span>
                </div>
              </div>
            </div>
          </section>

          {isStructureActive && (
            <section className="network-analysis-overview-panel__section">
              <div className="network-analysis-overview-panel__section-header">
                <h3 className="network-analysis-overview-panel__section-title">Perfil derivado</h3>
                <span className="network-analysis-overview-panel__section-pill">
                  <ChevronDown size={12} />
                  Ajuste de lectura
                </span>
              </div>
              <div className="network-analysis-overview-panel__select-wrapper">
                <select
                  className="network-analysis-overview-panel__select"
                  value={hierarchyProfile}
                  onChange={(event) => onHierarchyProfileChange(event.target.value as HierarchyProfile)}
                >
                  {Object.entries(HIERARCHY_PROFILE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <p className="network-analysis-overview-panel__hint">
                {HIERARCHY_PROFILE_DESCRIPTIONS[hierarchyProfile]}
              </p>
            </section>
          )}

          {!isPopulationActive && (
          <section className="network-analysis-overview-panel__section">
            <div className="network-analysis-overview-panel__section-header">
              <h3 className="network-analysis-overview-panel__section-title">Rango visual</h3>
              <span className="network-analysis-overview-panel__section-pill">
                <SlidersHorizontal size={12} />
                Depthmap-like
              </span>
            </div>

            <div
              className="network-analysis-overview-panel__gradient"
              style={{ background: `linear-gradient(to right, ${activeGradientStops})` }}
            />
            <div className="network-analysis-overview-panel__gradient-labels">
              <span>{formatMetricValue(activeVisualRange?.min)}</span>
              <span>{formatMetricValue(activeVisualRange?.max)}</span>
            </div>

            {activeDataRange && activeVisualRange ? (
              <div className="network-analysis-overview-panel__control-stack">
                <div className="network-analysis-overview-panel__slider-header">
                  <span>Mínimo visual</span>
                  <strong>{formatMetricValue(activeVisualRange.min, 4)}</strong>
                </div>
                <input
                  type="range"
                  className="network-analysis-overview-panel__slider"
                  min={activeDataRange.min}
                  max={activeDataRange.max - metricStep}
                  step={metricStep}
                  value={Math.min(activeVisualRange.min, activeVisualRange.max - metricStep)}
                  onChange={(event) => handleVisualMinChange(Number(event.target.value))}
                />

                <div className="network-analysis-overview-panel__slider-header">
                  <span>Máximo visual</span>
                  <strong>{formatMetricValue(activeVisualRange.max, 4)}</strong>
                </div>
                <input
                  type="range"
                  className="network-analysis-overview-panel__slider"
                  min={activeDataRange.min + metricStep}
                  max={activeDataRange.max}
                  step={metricStep}
                  value={Math.max(activeVisualRange.max, activeVisualRange.min + metricStep)}
                  onChange={(event) => handleVisualMaxChange(Number(event.target.value))}
                />

                <div className="network-analysis-overview-panel__slider-header">
                  <span>Curva de contraste</span>
                  <strong>{activeContrastExponent.toFixed(1)}x</strong>
                </div>
                <input
                  type="range"
                  className="network-analysis-overview-panel__slider"
                  min={0.6}
                  max={2.2}
                  step={0.1}
                  value={activeContrastExponent}
                  onChange={(event) => {
                    const nextValue = Number(event.target.value);
                    if (isStructureActive) {
                      setStructureContrastExponent(nextValue);
                      return;
                    }

                    setContrastExponent(nextValue);
                  }}
                />

                <p className="network-analysis-overview-panel__hint">
                  Baja la curva para abrir contraste en valores medios. Súbela para concentrar el color en los máximos.
                </p>

                <div className="network-analysis-overview-panel__control-row">
                  <span className="network-analysis-overview-panel__hint">
                    Rango real: {formatMetricValue(activeDataRange.min)} a {formatMetricValue(activeDataRange.max)}
                  </span>
                  <button
                    type="button"
                    className="network-analysis-overview-panel__ghost-btn"
                    onClick={handleResetVisualRange}
                    disabled={!hasCustomVisualRange && activeContrastExponent === 1}
                  >
                    Restablecer
                  </button>
                </div>
              </div>
            ) : (
              <p className="network-analysis-overview-panel__hint">
                Todavía no hay rango disponible para el indicador seleccionado.
              </p>
            )}
          </section>
          )}

          {!isPopulationActive && (
          <section className="network-analysis-overview-panel__section">
            <h3 className="network-analysis-overview-panel__section-title">Trazabilidad</h3>

            {!isStructureActive && (
              <>
                <div className="network-analysis-overview-panel__meta-row">
                  <span className="network-analysis-overview-panel__meta-label">Calculado:</span>
                  <span className="network-analysis-overview-panel__meta-value">
                    {calculatedAt
                      ? `${calculatedAt.toLocaleDateString('es-AR')} ${calculatedAt.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}`
                      : 'N/D'}
                  </span>
                </div>
                <div className="network-analysis-overview-panel__meta-row">
                  <span className="network-analysis-overview-panel__meta-label">Sintaxis:</span>
                  <span className="network-analysis-overview-panel__meta-value">v{syntaxVersion}</span>
                </div>
                <div className="network-analysis-overview-panel__meta-row">
                  <span className="network-analysis-overview-panel__meta-label">Basado en:</span>
                  <span className="network-analysis-overview-panel__meta-value">
                    Red v{syntaxBasedOnNetworkVersion ?? '?'}{isStale ? ` / actual v${urbanNetworkVersion}` : ''}
                  </span>
                </div>
              </>
            )}

            {isStructureActive && hierarchyData && (
              <>
                <div className="network-analysis-overview-panel__meta-row">
                  <span className="network-analysis-overview-panel__meta-label">Perfil:</span>
                  <span className="network-analysis-overview-panel__meta-value">
                    {HIERARCHY_PROFILE_LABELS[hierarchyProfile]}
                  </span>
                </div>
                <div className="network-analysis-overview-panel__meta-row">
                  <span className="network-analysis-overview-panel__meta-label">Segmentos:</span>
                  <span className="network-analysis-overview-panel__meta-value">
                    {hierarchyData.metadata.totalSegments.toLocaleString()}
                  </span>
                </div>
                <div className="network-analysis-overview-panel__meta-row">
                  <span className="network-analysis-overview-panel__meta-label">Pesos:</span>
                  <span className="network-analysis-overview-panel__meta-value">
                    Global {Math.round(hierarchyData.metadata.weights.global * 100)}% | Local {Math.round(hierarchyData.metadata.weights.local * 100)}%
                  </span>
                </div>
                <div className="network-analysis-overview-panel__meta-row">
                  <span className="network-analysis-overview-panel__meta-label">Actualizado:</span>
                  <span className="network-analysis-overview-panel__meta-value">
                    {new Date(hierarchyData.metadata.completedAt).toLocaleDateString('es-AR')}
                  </span>
                </div>
              </>
            )}
          </section>
          )}

          <section className="network-analysis-overview-panel__section">
            <div className="network-analysis-overview-panel__info-callout">
              <Info size={14} />
              <p>
                {isPopulationActive
                  ? 'Hacé clic en cualquier segmento del mapa para ver su detalle de población asignada.'
                  : 'Haz clic en un segmento para pasar al detalle del tramo. El panel de detalle seguirá el indicador activo del análisis.'}
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
